/**
 * `GamePlayUIPresenter`, minus the `RectTransform`s.
 *
 * The presenter's whole observable job is deciding when the two HUD buttons are
 * interactable, and gating clicks behind `BasePresenter`'s cooldown. None of
 * that needs a DOM, and keeping it out of one is what lets it be tested against
 * the decomp instead of against a screenshot — `gameUi.ts` is the view that
 * drives elements from this.
 *
 * Every method below names the decomp file it came from.
 */

/**
 * `BasePresenter` — 🔍 `AddButtonListener__0x20d3298.c` and its closure
 * `<AddButtonListener>b__0` (`__0x20d364c.c`):
 *
 *     float t;
 *     _buttonClickTimes.TryGetValue(button, out t);
 *     if (Time.unscaledTime - t < ButtonCooldown) return;   // const = 1f
 *     _buttonClickTimes[button] = Time.unscaledTime;
 *     action();
 *
 * `ButtonCooldown` is `private const float = 1` (`dump.cs:705840`), and the
 * clock is `Time.unscaledTime`, so a paused game does not pause the cooldown.
 *
 * Two details the shape of the decomp pins down, and that a "debounce" would
 * get wrong: the dictionary is keyed **per button**, so the two buttons do not
 * share a cooldown; and the *first* click on a button passes even though
 * `TryGetValue` leaves `t` at 0, because the guard is skipped entirely when the
 * key is absent.
 */
export class ButtonCooldownGate {
  static readonly COOLDOWN_SECONDS = 1

  private readonly lastClick = new Map<string, number>()

  /** True when the click gets through — i.e. when the action would run. */
  tryClick(button: string, unscaledTime: number): boolean {
    const previous = this.lastClick.get(button)
    if (previous !== undefined && unscaledTime - previous < ButtonCooldownGate.COOLDOWN_SECONDS) {
      return false
    }
    this.lastClick.set(button, unscaledTime)
    return true
  }

  /** A reload builds a new presenter in the original; here it clears the map. */
  clear(): void {
    this.lastClick.clear()
  }
}

export class GamePlayUiState {
  /** `Selectable.interactable` on `restartButton` (0x38). */
  restartInteractable = false
  /** `Selectable.interactable` on `settingsButton` (0x30). */
  settingsInteractable = false
  /** `_firstInputDetected` (0x78). */
  firstInputDetected = false

  /**
   * `LevelLoaded(int)` — 🔍 `__0x21472c8.c`: after writing the level text it
   * calls `SetRestartButtonInteractable(false)` then
   * `SetSettingsButtonInteractable(true)`.
   *
   * So the level opens with restart **off**: there is nothing to undo yet.
   */
  levelLoaded(): void {
    this.firstInputDetected = false
    this.restartInteractable = false
    this.settingsInteractable = true
  }

  /**
   * `OnFirstInputDetected` — 🔍 `__0x21474a8.c`:
   *
   *     _firstInputDetected = true;
   *     if (_boosterIsActive) return;
   *     SetRestartButtonInteractable(true);
   *
   * `_boosterIsActive` (0x79) is not modelled: across all 19 methods of the
   * class it is written once — by `OnLevelLoaded`, with 0 — and read once, here.
   * The gate never closes, so porting it would be porting a constant.
   */
  firstInput(): void {
    this.firstInputDetected = true
    this.restartInteractable = true
  }

  /**
   * `OnAllBusesDispatched` — 🔍 `__0x21474c4.c`. The whole body is
   * `SetRestartButtonInteractable(false)` inlined. Settings is untouched.
   */
  allBusesDispatched(): void {
    this.restartInteractable = false
  }

  /**
   * `OnLevelFailed` — 🔍 `__0x214747c.c`: `SetRestartButtonInteractable(false)`
   * and then, falling into the twin, settings off too.
   */
  levelFailed(): void {
    this.restartInteractable = false
    this.settingsInteractable = false
  }

  /**
   * `OnLevelSucceeded` — 🔍 `__0x2147450.c` has the same body as `OnLevelFailed`,
   * but **no code in the binary constructs a `LevelSucceededEvent`**: the type
   * appears twice in `dump.cs` (its declaration and the handler's signature) and
   * its `.ctor` has no caller. A win therefore leaves the HUD alone, and this
   * method exists to say so rather than to be called.
   */
  levelSucceeded(): void {
    /* dead branch — see the comment. */
  }

  /**
   * `OnRestartButtonClicked` — 🔍 `__0x21471ac.c` opens with
   * `SetRestartButtonInteractable(false)` before it hands over to the model, so
   * the button cannot be pressed twice while the reload is in flight.
   */
  restartClicked(): void {
    this.restartInteractable = false
  }
}
